<template>
  <div class="login-wrapper">
    <!-- Left Side: Form Container -->
    <div class="login-left">
      <div class="login-form-container fade-in-up">
        
        <!-- Brand Header -->
        <div class="brand-header">
          <img src="https://pub-3f3d3f8847a94e1e89883126e9a8db4c.r2.dev/config/logo.png" alt="Servease Logo" class="brand-logo" />
        </div>
        
        <div class="welcome-text">
          <h1>Bem-vindo de volta</h1>
          <p>Acesse sua conta para continuar impulsionando o seu negócio.</p>
        </div>

        <!-- Formulário -->
        <form @submit.prevent="login" novalidate class="modern-form">
          <!-- E-mail -->
          <div class="form-group">
            <label for="usuario">E-mail corporativo</label>
            <div class="input-wrapper">
              <i class="bi bi-envelope"></i>
              <input
                id="usuario"
                v-model="usuario"
                type="email"
                placeholder="nome@empresa.com"
                required
              />
            </div>
          </div>

          <!-- Senha -->
          <div class="form-group">
            <label for="senha">Sua senha</label>
            <div class="input-wrapper">
              <i class="bi bi-lock"></i>
              <input
                id="senha"
                v-model="senha"
                :type="showPassword ? 'text' : 'password'"
                placeholder="••••••••"
                required
                minlength="6"
              />
              <button 
                type="button" 
                class="toggle-password" 
                @click="showPassword = !showPassword"
                aria-label="Toggle password visibility"
              >
                <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
              </button>
            </div>
          </div>

          <!-- Actions: Remember Me & Forgot Password -->
          <div class="form-actions">
            <label class="remember-me">
              <input id="lembrar" type="checkbox" v-model="lembrarLogin" />
              <span class="custom-checkbox"></span>
              <span class="label-text">Lembrar-me</span>
            </label>
            <router-link class="forgot-link" to="/esqueci-senha">Esqueceu a senha?</router-link>
          </div>

          <!-- Botão Submeter -->
          <button class="btn-entrar" type="submit">
            <span>Entrar na Plataforma</span>
            <i class="bi bi-arrow-right"></i>
          </button>
        </form>

      </div>
    </div>

    <!-- Right Side: Brand Showcase -->
    <div class="login-right d-none d-lg-flex">
      <div class="brand-showcase fade-in-right">
        <div class="showcase-content glass-card">
          <div class="quote-icon"><i class="bi bi-quote"></i></div>
          <h2 class="showcase-title">Pessoas e processos, integrados.</h2>
          <p class="showcase-text">
            O Servease revoluciona a gestão e prestação de serviços, unindo uma interface amigável com a tecnologia necessária para levar sua empresa ao próximo nível.
          </p>
          <div class="showcase-author">
            <strong>Equipe Servease</strong>
            <span>Inovação Contínua</span>
          </div>
        </div>

        <!-- Abstract fluid shapes mimicking spheres but SaaS style -->
        <div class="background-decorations">
          <div class="blob blob-1"></div>
          <div class="blob blob-2"></div>
        </div>
      </div>
    </div>

    <!-- Bootstrap Toast para erros -->
    <div class="toast-container position-fixed bottom-0 end-0 p-3">
      <div id="liveToast" class="toast" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="toast-header">
          <i class="bi bi-exclamation-circle text-danger me-2"></i>
          <strong class="me-auto text-danger">Alerta Servease</strong>
          <button type="button" class="btn-close" data-bs-dismiss="toast" aria-label="Close"></button>
        </div>
        <div class="toast-body" id="toastBody">
          Aqui aparecerá a mensagem de erro.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from '@/axios.js';
import { showToast } from '@/eventBus.js';
import { useAuthStore } from '@/stores/auth';
import { useRouter } from 'vue-router';

const auth = useAuthStore();
const usuario = ref('');
const senha = ref('');
const lembrarLogin = ref(false);
const showPassword = ref(false);
const router = useRouter();

onMounted(() => {
  const savedUser = localStorage.getItem('savedUser');
  const savedPass = localStorage.getItem('savedPass');
  if (savedUser && savedPass) {
    usuario.value = savedUser;
    senha.value = savedPass;
    lembrarLogin.value = true;
  }
});

function login() {
  if (!usuario.value || !senha.value) {
    showToast("Preencha todos os campos obrigatórios.");
    return;
  }
  if (!/\S+@\S+\.\S+/.test(usuario.value)) {
    showToast("Por favor, digite um e-mail válido.");
    return;
  }
  if (senha.value.length < 6) {
    showToast("A senha deve conter no mínimo 6 caracteres.");
    return;
  }

  localStorage.removeItem('token');
  localStorage.removeItem('idCompany');
  axios.post('/auth/login', {
    username: usuario.value,
    password: senha.value,
  })
    .then(response => {
      localStorage.setItem('idCompany', response.data.id);
      localStorage.setItem('nameUser', response.data.name);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      auth.loginWithToken(response.data.accessToken);

      // Save credentials if "Lembrar-me" is checked
      if (lembrarLogin.value) {
        localStorage.setItem('savedUser', usuario.value);
        localStorage.setItem('savedPass', senha.value);
      } else {
        localStorage.removeItem('savedUser');
        localStorage.removeItem('savedPass');
      }

      router.push('/home');
    })
    .catch(error => {
      showToast(error.response?.data?.message || "Credenciais inválidas ou erro no servidor.");
    });
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

* {
  box-sizing: border-box;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

/* Base Layout Wrapper */
.login-wrapper {
  display: flex;
  min-height: 100vh;
  width: 100%;
  background-color: #FAFAF9; /* Ultra light warm grey */
  overflow: hidden;
}

/* ---------- LEFT SIDE: FORM ---------- */
.login-left {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: #FFFFFF;
  position: relative;
  z-index: 2;
  box-shadow: 4px 0 24px rgba(0,0,0,0.02);
}

.login-form-container {
  width: 100%;
  max-width: 440px;
}

/* Brand */
.brand-header {
  margin-bottom: 2rem;
}

.brand-logo {
  height: 48px;
  object-fit: contain;
  transition: transform 0.3s ease;
  float: none !important;
  margin: 0 !important;
}

.brand-logo:hover {
  transform: scale(1.02);
}

/* Typography */
.welcome-text {
  margin-bottom: 2.5rem;
}

.welcome-text h1 {
  font-size: 2rem;
  font-weight: 700;
  color: #1E293B; /* Deep Slate */
  margin-bottom: 0.5rem;
  line-height: 1.2;
}

.welcome-text p {
  font-size: 0.95rem;
  color: #64748B;
  font-weight: 500;
  line-height: 1.5;
}

/* Modern Form */
.modern-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.5rem;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 12px;
  transition: all 0.3s ease;
  overflow: hidden;
}

.input-wrapper:focus-within {
  background-color: #FFFFFF;
  border-color: #DF523A;
  box-shadow: 0 0 0 4px rgba(223, 82, 58, 0.1);
}

.input-wrapper i.bi {
  padding-left: 1rem;
  color: #94A3B8;
  font-size: 1.1rem;
  transition: color 0.3s ease;
}

.input-wrapper:focus-within i.bi {
  color: #DF523A;
}

.input-wrapper input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.875rem 1rem;
  font-size: 0.95rem;
  color: #0F172A;
  outline: none;
}

.input-wrapper input::placeholder {
  color: #94A3B8;
  font-weight: 500;
}

.toggle-password {
  background: transparent;
  border: none;
  padding: 0 1rem;
  color: #94A3B8;
  cursor: pointer;
  outline: none;
  transition: color 0.3s ease;
}

.toggle-password:hover {
  color: #DF523A;
}

/* Form Actions (Remember / Forgot) */
.form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: -0.5rem;
}

.remember-me {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;
}

.remember-me input {
  display: none;
}

.custom-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 2px solid #CBD5E1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  background: #fff;
}

.remember-me input:checked + .custom-checkbox {
  background-color: #DF523A;
  border-color: #DF523A;
}

.remember-me input:checked + .custom-checkbox::after {
  content: '';
  width: 4px;
  height: 8px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
  margin-bottom: 2px;
}

.label-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: #475569;
}

.forgot-link {
  font-size: 0.9rem;
  font-weight: 600;
  color: #DF523A;
  text-decoration: none;
  transition: color 0.3s ease;
}

.forgot-link:hover {
  color: #c9402a;
}

/* Submit Button */
.btn-entrar {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #DF523A 0%, #E86A4C 100%);
  color: #FFFFFF;
  border: none;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 8px 16px rgba(223, 82, 58, 0.25);
  margin-top: 1rem;
}

.btn-entrar:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 20px rgba(223, 82, 58, 0.35);
}

.btn-entrar:active {
  transform: translateY(0);
}


/* ---------- RIGHT SIDE: BRAND SHOWCASE ---------- */
.login-right {
  flex: 1.2;
  position: relative;
  background: radial-gradient(circle at top right, #FDF9F3, #FAFAF9, #F1EAD7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  overflow: hidden;
}

.background-decorations {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.5;
  animation: float 10s infinite alternate ease-in-out;
}

.blob-1 {
  width: 400px;
  height: 400px;
  background: #DF523A;
  top: -100px;
  right: -100px;
  opacity: 0.15;
}

.blob-2 {
  width: 500px;
  height: 500px;
  background: #F1EAD7;
  bottom: -150px;
  left: -100px;
  animation-delay: -5s;
}

/* Glass Card */
.glass-card {
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 24px;
  padding: 3rem;
  max-width: 520px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.05);
}

.quote-icon {
  font-size: 2.5rem;
  color: #DF523A;
  margin-bottom: 1rem;
  opacity: 0.8;
}

.showcase-title {
  font-size: 2rem;
  font-weight: 800;
  color: #1E293B;
  margin-bottom: 1rem;
  line-height: 1.2;
}

.showcase-text {
  font-size: 1.1rem;
  color: #475569;
  line-height: 1.6;
  margin-bottom: 2rem;
  font-weight: 500;
}

.showcase-author {
  display: flex;
  flex-direction: column;
}

.showcase-author strong {
  font-size: 1rem;
  color: #0F172A;
  font-weight: 700;
}

.showcase-author span {
  font-size: 0.9rem;
  color: #64748B;
  font-weight: 500;
}

/* ---------- ANIMATIONS ---------- */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(40px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(30px, 30px) scale(1.05); }
  100% { transform: translate(-20px, 40px) scale(0.95); }
}

.fade-in-up {
  animation: fadeInUp 0.8s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

.fade-in-right {
  animation: fadeInRight 1s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
}

/* Toast Z-index */
.toast-container {
  z-index: 9999;
}
</style>
